<template>
  <v-menu :close-on-content-click="false" v-model="menuOpen" :disabled="disabled">
    <template v-slot:activator="{ props: menuProps }">
      <v-text-field
        :model-value="modelValue"
        :label="label"
        :rules="rules"
        :disabled="disabled"
        :clearable="clearable"
        :hide-details="hideDetails"
        :error-messages="errorMessages"
        readonly
        v-bind="{ ...menuProps, ...$attrs }"
        append-inner-icon="fa-calendar"
        @click:clear="onClear"
      />
    </template>
    <!-- color: without it the chosen day is a flat button in the theme's surface-variant, a
         near-black disc with a dark digit; primary is what the week picker of the assignments
         page already uses. -->
    <v-date-picker
      v-model="pickerDate"
      @update:model-value="onDateSelected"
      first-day-of-week="0"
      density="compact"
      color="primary"
      hide-header
      :min="min"
      :max="max"
    />
  </v-menu>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { formatDateForInput } from '@/utils/date-utils';

const props = withDefaults(defineProps<{
  modelValue: string | null;
  label?: string;
  rules?: ((value: string | null) => boolean | string)[];
  disabled?: boolean;
  clearable?: boolean;
  hideDetails?: boolean | 'auto';
  errorMessages?: string | string[];
  min?: string;
  max?: string;
}>(), {
  label: '',
  rules: () => [],
  disabled: false,
  clearable: false,
  hideDetails: false,
  errorMessages: '',
  min: undefined,
  max: undefined
});

const emit = defineEmits<{
  'update:modelValue': [value: string | null];
}>();

defineOptions({
  inheritAttrs: false
});

const menuOpen = ref(false);
const pickerDate = ref<Date | null>(null);

// Sync pickerDate when modelValue changes externally
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    pickerDate.value = new Date(newValue + 'T00:00:00');
  } else {
    pickerDate.value = null;
  }
}, { immediate: true });

function onDateSelected(date: Date | null) {
  if (date) {
    emit('update:modelValue', formatDateForInput(date));
  }
  menuOpen.value = false;
}

function onClear() {
  emit('update:modelValue', null);
  pickerDate.value = null;
}
</script>
